import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '../router'

// 菜单项类型（后端返回的原始菜单结构）
export interface MenuItem {
  id: string
  name: string
  path: string
  component?: string
  icon?: string
  hidden?: boolean
  children?: MenuItem[]
  permissions?: string[]
  [key: string]: any
}

// 用户信息类型
export interface UserInfo {
  uid: string
  username: string
  nickname: string
  avatar: string
  email: string
  phone: string
  roles: string[]
}

export const useUserStore = defineStore('user', () => {
  const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')
  const basePath = (import.meta.env.VITE_API_BASE_PATH || '').replace(/\/$/, '')
  // State
  const token = ref<string>(localStorage.getItem('access_token') || '')
  const refreshToken = ref<string>(localStorage.getItem('refresh_token') || '')
  const userInfo = ref<UserInfo | null>(null)
  const menus = ref<MenuItem[]>([])
  const permissions = ref<string[]>([])
  const isRoutesLoaded = ref<boolean>(false)

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const hasPermission = computed(() => (permission: string) => {
    return permissions.value.includes(permission) || permissions.value.includes('*:*:*')
  })

  // Actions
  
  // 设置token
  const setToken = (newToken: string, newRefreshToken?: string) => {
    token.value = newToken
    localStorage.setItem('access_token', newToken)
    if (newRefreshToken) {
      refreshToken.value = newRefreshToken
      localStorage.setItem('refresh_token', newRefreshToken)
    }
  }

  // 清除token
  const clearToken = () => {
    token.value = ''
    refreshToken.value = ''
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}${basePath}/auth/user`, {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })
      const data = await response.json()
      if (data.code === 200 && data.data) {
        userInfo.value = data.data
        return data.data
      }
      return null
    } catch (error) {
      console.error('获取用户信息失败:', error)
      return null
    }
  }

  // 获取用户菜单和权限
  const fetchUserMenus = async () => {
    try {
      console.log('开始获取用户菜单，token:', token.value)
      
      // 并行获取菜单树和权限列表
      const [menuRes, permsRes] = await Promise.all([
        fetch(`${apiBaseUrl}${basePath}/menu/user-tree`, {
          headers: { 'Authorization': `Bearer ${token.value}` }
        }),
        fetch(`${apiBaseUrl}${basePath}/menu/user-perms`, {
          headers: { 'Authorization': `Bearer ${token.value}` }
        })
      ])
      
      const menuData = await menuRes.json()
      const permsData = await permsRes.json()
      console.log('获取用户菜单响应:', menuData)
      
      if (menuData.code !== 200 || !menuData.data) {
        console.error('获取用户菜单失败:', menuData.message, 'code:', menuData.code, 'data:', menuData.data)
        return []
      }
      
      // 转换后端菜单数据为前端格式
      const convertMenu = (menu: any): MenuItem => {
        const item: MenuItem = {
          id: menu.id?.toString() || '',
          name: menu.name,
          path: menu.path,
          component: menu.component,
          icon: menu.icon,
          permissions: menu.perms ? [menu.perms] : []
        }
        
        if (menu.children && menu.children.length > 0) {
          item.children = menu.children.map(convertMenu)
        }
        
        return item
      }
      
      const menuList = menuData.data.map(convertMenu)
      menus.value = menuList
      
      // 使用独立的权限接口获取所有权限（包括按钮权限）
      if (permsData.code === 200 && permsData.data) {
        permissions.value = permsData.data
      } else {
        // 备用：从菜单树提取权限
        const allPermissions: string[] = []
        function extractPermissions(menus: MenuItem[]) {
          menus.forEach(menu => {
            if (menu.permissions) {
              allPermissions.push(...menu.permissions)
            }
            if (menu.children && menu.children.length > 0) {
              extractPermissions(menu.children)
            }
          })
        }
        extractPermissions(menuList)
        permissions.value = allPermissions
      }
      
      return menuList
    } catch (error) {
      console.error('获取用户菜单失败:', error)
      return []
    }
  }

  // 登录
  const login = async (loginData: { username: string; password: string }) => {
    try {
      const response = await fetch(`${apiBaseUrl}${basePath}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      })
      const data = await response.json()
      if (data.code === 200 && data.data) {
        setToken(data.data.token, data.data.refreshToken)
        return true
      }
      return false
    } catch (error) {
      console.error('登录失败:', error)
      return false
    }
  }

  // 登出
  const logout = async () => {
    try {
      await fetch(`${apiBaseUrl}${basePath}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token.value}` }
      })
    } catch (error) {
      console.error('登出失败:', error)
    } finally {
      clearToken()
      userInfo.value = null
      menus.value = []
      permissions.value = []
      isRoutesLoaded.value = false
      
      // 重置路由
      resetRouter()
      
      router.push('/login')
    }
  }

  // 重置路由
  const resetRouter = () => {
    // 移除所有动态添加的路由
    const routes = router.getRoutes()
    routes.forEach(route => {
      if (route.name && !['Login', 'AuthCallback'].includes(route.name as string)) {
        router.removeRoute(route.name)
      }
    })
    isRoutesLoaded.value = false
  }

  return {
    token,
    refreshToken,
    userInfo,
    menus,
    permissions,
    isRoutesLoaded,
    isLoggedIn,
    hasPermission,
    setToken,
    clearToken,
    fetchUserInfo,
    fetchUserMenus,
    login,
    logout,
    resetRouter
  }
})
